import React, {
  useEffect, useMemo, useState,
} from 'react';
import {
  Alert, Button, Checkbox, Input, Select, Spin, Typography,
} from 'antd';
import { WhatsAppOutlined } from '@ant-design/icons';

import {
  WhatsAppTemplate,
  getWhatsAppTemplates,
  previewWhatsAppTemplate,
  renderTemplatePreview,
  templateKindLabel,
  apiErrorMessage,
} from '../../reducers/whatsapp/resources';

const { Text } = Typography;

export interface WhatsAppTemplateSend {
  templateId: string;
  /** Automatic use of the template (null = manual only), sent for older backends. */
  templateKind: string | null;
  /** Values for the template's `inputs`, in order. */
  inputs: string[];
  /** The rendered preview text (the API requires `body`). */
  body: string;
}

export interface WhatsAppTemplatePanelProps {
  firstName: string;
  lastName: string;
  userName: string;
  companyName: string;
  /** Phone number passed /cansend — the send button stays disabled until then. */
  phoneValid: boolean;
  /** Existing contact on the number, so the preview shows their real fields. */
  contactId?: string | null;
  onSend(payload: WhatsAppTemplateSend): Promise<void>;
}

/** "gohire_interview_invite · Interview invite · en" */
export const templateOptionLabel = (t: WhatsAppTemplate): string => (
  [t.name, t.kindLabel || templateKindLabel(t.kind), t.language].filter(Boolean).join(' · ')
);

/**
 * WhatsApp branch of the New Message modal (ADR-037).
 *
 * A business-initiated WhatsApp conversation must open with a Meta-approved
 * template, so instead of the free-form composer the recruiter picks one of
 * the sender's mapped templates, fills only the "ask when sending" fields,
 * confirms the candidate's opt-in and sends. Every other placeholder (contact
 * fields such as {{workday_position}}, recruiter, company) is resolved by the
 * server from the template's mapping.
 */
const WhatsAppTemplatePanel: React.FC<WhatsAppTemplatePanelProps> = ({
  firstName,
  companyName,
  phoneValid,
  contactId = null,
  onSend,
}) => {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [inputValues, setInputValues] = useState<Record<number, string>>({});
  const [optIn, setOptIn] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getWhatsAppTemplates()
      .then((rows) => {
        if (cancelled) return;
        setTemplates(rows);
        if (rows.length === 1) setSelectedId(rows[0].id);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(apiErrorMessage(err, 'Failed to load WhatsApp templates.'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const template = useMemo(
    () => templates.find((t) => t.id === selectedId),
    [templates, selectedId],
  );

  const inputs = template ? template.inputs || [] : [];
  const values = inputs.map((i) => (inputValues[i.index] || '').trim());
  const localPreview = template ? renderTemplatePreview(template, inputValues) : '';
  const [serverPreview, setServerPreview] = useState<{ key: string; text: string } | null>(null);
  const previewKey = template ? JSON.stringify([template.id, contactId, values]) : '';

  // Server preview (real contact fields, typed inputs); the local render shows meanwhile.
  useEffect(() => {
    if (!template) return undefined;
    let cancelled = false;
    const key = previewKey;
    const t = setTimeout(() => {
      previewWhatsAppTemplate(template.id, { contactId, inputs: values })
        .then((text) => { if (!cancelled && text) setServerPreview({ key, text }); })
        .catch(() => { /* keep the local render */ });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [previewKey]);

  const preview = serverPreview && serverPreview.key === previewKey ? serverPreview.text : localPreview;
  const allFilled = inputs.every((input, i) => input.required === false || !!values[i]);
  const canSend = phoneValid && !!template && allFilled && optIn && !sending;

  const candidate = firstName.trim() || 'the candidate';
  const candidateCap = firstName.trim() || 'This candidate';
  const company = companyName || 'your company';
  const timestamp = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  const handleSend = async () => {
    if (!template || !canSend) return;
    setSending(true);
    setSendError(null);
    try {
      await onSend({
        templateId: template.id,
        templateKind: template.kind || null,
        inputs: values,
        body: preview,
      });
    } catch (err) {
      setSendError(apiErrorMessage(err, 'Failed to send the WhatsApp template.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="whatsapp-template-panel" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Alert
        type="info"
        showIcon
        icon={<WhatsAppOutlined />}
        message={(
          <span>
            A new WhatsApp conversation must start with a Meta-approved template.
            {' '}Once {candidate} replies, you can message free-form for 24 hours after each reply.
          </span>
        )}
      />

      {loadError && <Alert type="error" showIcon message={loadError} />}

      <div>
        <div style={{ marginBottom: 4, fontWeight: 500 }}>Template</div>
        <Select
          data-testid="whatsapp-template-select"
          style={{ width: '100%' }}
          placeholder={loading ? 'Loading templates…' : 'Choose an approved template'}
          loading={loading}
          disabled={loading || templates.length === 0}
          value={selectedId}
          onChange={(id: string) => { setSelectedId(id); setInputValues({}); setSendError(null); }}
          options={templates.map((t) => ({ value: t.id, label: templateOptionLabel(t) }))}
          notFoundContent={loading ? <Spin size="small" /> : 'No approved templates for this company'}
        />
      </div>

      {template && inputs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {inputs.map((input) => (
            <div key={input.index}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>
                {input.label}
                {input.required === false && (
                  <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                    {' · optional, filled automatically when left empty'}
                  </Text>
                )}
              </div>
              <Input
                data-testid={`whatsapp-input-${input.index}`}
                value={inputValues[input.index] || ''}
                placeholder={input.label}
                autoComplete="off"
                maxLength={200}
                onChange={(e) => {
                  const { value } = e.target;
                  setInputValues((prev) => ({ ...prev, [input.index]: value }));
                }}
              />
            </div>
          ))}
        </div>
      )}

      {template && (
        <div>
          <div style={{ marginBottom: 4, fontWeight: 500 }}>Preview</div>
          <div
            style={{
              background: '#e5ddd5',
              borderRadius: 8,
              padding: '12px 12px 12px 48px',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <div
              data-testid="whatsapp-preview-bubble"
              style={{
                background: '#dcf8c6',
                color: '#111b21',
                borderRadius: '8px 0 8px 8px',
                padding: '6px 8px 8px 10px',
                maxWidth: '100%',
                boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
                fontSize: 14,
                lineHeight: '19px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {preview}
              <div style={{ textAlign: 'right', fontSize: 11, color: '#667781', marginTop: 4 }}>
                {timestamp}
              </div>
            </div>
          </div>
        </div>
      )}

      <div>
        <Checkbox
          data-testid="whatsapp-optin-checkbox"
          checked={optIn}
          onChange={(e) => setOptIn(e.target.checked)}
        >
          {candidateCap} has opted in to receive WhatsApp messages from {company}.
        </Checkbox>
        <div style={{ marginLeft: 24 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Required by WhatsApp. Recorded on the contact.</Text>
        </div>
      </div>

      {sendError && <Alert type="error" showIcon message={sendError} />}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          data-testid="whatsapp-send-template"
          type="primary"
          icon={<WhatsAppOutlined />}
          style={canSend ? { backgroundColor: '#25D366', borderColor: '#25D366' } : undefined}
          disabled={!canSend}
          loading={sending}
          onClick={handleSend}
        >
          Send template
        </Button>
      </div>
    </div>
  );
};

export default WhatsAppTemplatePanel;
